/**
 * Déroulant de thème — trois états, Clair / Sombre / Système (coquille.md §6.2).
 *
 * « Système » RETIRE `data-theme` et rend la main à `prefers-color-scheme` : un produit qui ne
 * propose que deux états rend le mode système inatteignable après le premier clic. Le
 * déclencheur montre l'icône du mode courant plus son libellé écrit — l'icône seule ne
 * suffit pas, le libellé seul non plus.
 *
 * ⚠️ `onPersist` n'est pas optionnel par confort : depuis que le serveur est la source de
 * vérité du thème, un choix fait ICI et non persisté est **révoqué au rechargement suivant**,
 * quand la réponse de `GET /me/preferences` réimpose la valeur stockée. Le déroulant écrivait
 * seulement `localStorage`, si bien que l'écran repassait en clair sous les yeux de
 * l'utilisateur quelques centaines de millisecondes après le chargement. Il reste sans
 * `onPersist` sur les surfaces non authentifiées, où il n'y a rien à persister.
 */

import { useTranslation } from 'react-i18next';

import { useTheme } from '../../hooks/useTheme.js';
import { DD_LABEL, DD_TRIGGER, MENU_CHECK_OFF, MENU_CHECK_ON, MENU_RADIO } from '../../ui/theme.js';

import { Chevron, Deroulant } from './Deroulant.jsx';

const MODES = ['light', 'dark', 'system'];

/**
 * @param {object} props
 * @param {((theme: string) => void)|null} [props.onPersist] Enregistre le choix côté serveur.
 *   Omis sur les surfaces sans session : le thème reste alors local au navigateur.
 */
export default function MenuTheme({ onPersist = null }) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const Icone = ICONES[theme] ?? ICONES.system;

  return (
    <Deroulant
      label={t('theme.menu')}
      declencheur={({ ouvert }) => (
        <span className={DD_TRIGGER}>
          <Icone />
          <span className={DD_LABEL}>{t(`theme.${theme}`)}</span>
          <Chevron ouvert={ouvert} />
        </span>
      )}
    >
      {MODES.map((mode) => {
        const IconeMode = ICONES[mode];
        const choisi = theme === mode;
        return (
          <button
            key={mode}
            type="button"
            role="menuitemradio"
            aria-checked={choisi}
            className={MENU_RADIO}
            onClick={() => {
              setTheme(mode);
              onPersist?.(mode);
            }}
          >
            <IconeMode />
            {t(`theme.${mode}`)}
            <span className={choisi ? MENU_CHECK_ON : MENU_CHECK_OFF} aria-hidden="true" />
          </button>
        );
      })}
    </Deroulant>
  );
}

/* Icônes en traits 2 px, géométrie carrée — la grammaire du système, pas un jeu d'icônes tiers. */
const svg = 'h-3.5 w-3.5 shrink-0';

function Soleil() {
  return (
    <svg viewBox="0 0 16 16" className={svg} fill="none" aria-hidden="true">
      <rect x="5" y="5" width="6" height="6" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M13 3l-1.5 1.5M4.5 11.5L3 13"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="square"
      />
    </svg>
  );
}
function Lune() {
  return (
    <svg viewBox="0 0 16 16" className={svg} fill="none" aria-hidden="true">
      <path
        d="M13 10.5A6 6 0 0 1 5.5 3 6 6 0 1 0 13 10.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="miter"
      />
    </svg>
  );
}
function Ecran() {
  return (
    <svg viewBox="0 0 16 16" className={svg} fill="none" aria-hidden="true">
      <rect x="1.5" y="2.5" width="13" height="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 13.5h6M8 10.5v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" />
    </svg>
  );
}
const ICONES = { light: Soleil, dark: Lune, system: Ecran };
